import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import useThemeStyles from '../../../hooks/useThemeStyles';

// Multiplier cycle on cell click
const CYCLE = [1, 2, 0.5, 0];

const formatMultiplier = (value) => {
  if (value === 0.5) return '½';
  if (value === 0) return '0';
  return `${value}×`;
};

/**
 * Editable damage-effectiveness grid (rows attack, columns defend).
 */
const TypeChartSection = ({ typeChart, onChange }) => {
  const { colors } = useThemeStyles();
  const [newType, setNewType] = useState('');

  const types = typeChart?.types || [];
  const multipliers = typeChart?.multipliers || [];

  const cellColor = (value) => {
    if (value === 2) return colors.semantic.success;
    if (value === 0.5) return colors.semantic.warning;
    if (value === 0) return colors.semantic.error;
    return 'transparent';
  };

  const addType = () => {
    const name = newType.trim();
    if (!name || types.some((t) => t.toLowerCase() === name.toLowerCase())) return;
    const nextTypes = [...types, name];
    const nextMultipliers = multipliers.map((row) => [...row, 1]);
    nextMultipliers.push(new Array(nextTypes.length).fill(1));
    onChange({ types: nextTypes, multipliers: nextMultipliers });
    setNewType('');
  };

  const removeType = (index) => {
    const nextTypes = types.filter((_, i) => i !== index);
    const nextMultipliers = multipliers
      .filter((_, i) => i !== index)
      .map((row) => row.filter((_, i) => i !== index));
    onChange({ types: nextTypes, multipliers: nextMultipliers });
  };

  const cycleCell = (row, col) => {
    const current = multipliers[row]?.[col] ?? 1;
    const next = CYCLE[(CYCLE.indexOf(current) + 1) % CYCLE.length];
    const nextMultipliers = multipliers.map((r, i) =>
      i === row ? r.map((value, j) => (j === col ? next : value)) : r
    );
    onChange({ types, multipliers: nextMultipliers });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ color: colors.text.secondary }}>
        Rows attack, columns defend. Click a cell to cycle 1× → 2× → ½× → 0×.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          label="New type"
          value={newType}
          onChange={(e) => setNewType(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addType();
          }}
          sx={{
            '& .MuiInputLabel-root': { color: colors.text.secondary },
            input: { color: colors.text.primary },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: colors.border.subtle },
            },
          }}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addType}
          disabled={!newType.trim()}
          sx={{ textTransform: 'none' }}
        >
          Add Type
        </Button>
      </Box>

      {types.length === 0 ? (
        <Typography variant="body2" sx={{ color: colors.text.secondary }}>
          Add types (e.g. Fire, Water, Grass) to build your effectiveness chart.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Box component="th" sx={{ p: 1 }} />
                {types.map((type, colIndex) => (
                  <Box component="th" key={type} sx={{ p: 0.5, verticalAlign: 'bottom' }}>
                    <Chip
                      label={type}
                      size="small"
                      onDelete={() => removeType(colIndex)}
                      deleteIcon={<CloseIcon />}
                      sx={{
                        backgroundColor: colors.sidebar.headerBackground,
                        color: colors.text.primary,
                      }}
                    />
                  </Box>
                ))}
              </tr>
            </thead>
            <tbody>
              {types.map((attackType, rowIndex) => (
                <tr key={attackType}>
                  <Box component="td" sx={{ p: 0.5, textAlign: 'right' }}>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.text.primary, fontWeight: 500, pr: 1 }}
                    >
                      {attackType}
                    </Typography>
                  </Box>
                  {types.map((defendType, colIndex) => {
                    const value = multipliers[rowIndex]?.[colIndex] ?? 1;
                    return (
                      <Box component="td" key={defendType} sx={{ p: 0.25 }}>
                        <Tooltip title={`${attackType} → ${defendType}: ${value}×`}>
                          <IconButton
                            size="small"
                            onClick={() => cycleCell(rowIndex, colIndex)}
                            sx={{
                              width: 44,
                              height: 36,
                              borderRadius: 1,
                              fontSize: '0.8rem',
                              color: value === 1 ? colors.text.secondary : colors.text.primary,
                              backgroundColor: cellColor(value),
                              border: `1px solid ${colors.border.subtle}`,
                              '&:hover': { borderColor: colors.interactive.primary },
                            }}
                          >
                            {formatMultiplier(value)}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}

      {types.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[
            { value: 2, label: 'Super effective (2×)' },
            { value: 1, label: 'Normal (1×)' },
            { value: 0.5, label: 'Not very effective (½×)' },
            { value: 0, label: 'No effect (0×)' },
          ].map((item) => (
            <Box key={item.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: 0.5,
                  backgroundColor: cellColor(item.value),
                  border: `1px solid ${colors.border.subtle}`,
                }}
              />
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

TypeChartSection.propTypes = {
  typeChart: PropTypes.shape({
    types: PropTypes.arrayOf(PropTypes.string),
    multipliers: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }),
  onChange: PropTypes.func.isRequired,
};

TypeChartSection.defaultProps = {
  typeChart: { types: [], multipliers: [] },
};

export default TypeChartSection;
